import type { ElementType, ReactElement, ReactNode } from "react";
import type { ComponentPropsWithoutRef } from "react";
import { isValidElement } from "react";
import type { BlockCategory } from "@circle-react-shared/BuilderUI/DataModel.types";

interface ElementWithBlocks {
  blocks?: BlockCategory;
}

interface ElementWithBlocks {
  blocks?: BlockCategory;
}

const isElementWithBlocksProps = (
  element: ReactElement,
): element is ReactElement<ElementWithBlocks> => "blocks" in element.props;

export const hasBlocksWithChildren = (
  node: ReactNode,
): node is ReactElement<ElementWithBlocks> => {
  if (!isValidElement(node)) {
    return false;
  }

  if (!isElementWithBlocksProps(node)) {
    return false;
  }

  const { blocks } = node.props; // No more type assertion needed

  if (blocks == null) {
    return false;
  }

  if (Array.isArray(blocks)) {
    return blocks.length > 0;
  }

  if (typeof blocks === "object") {
    return Object.keys(blocks).length > 0;
  }

  return Boolean(blocks);
};

interface BlockNodeProps extends ComponentPropsWithoutRef<"div"> {
  as?: ElementType;
  block?: ReactNode;
  children?: ReactNode;
  render?: (content: ReactNode) => ReactNode;
}

export const BlockSectionNestedBlock = ({
  children,
  block,
  render,
  as: Component = "div",
  ...rest
}: BlockNodeProps) => {
  const content = block ?? children;

  if (content == null) return null;

  const rendered = render ? render(content) : content;

  if (hasBlocksWithChildren(content)) {
    return <Component {...rest}>{rendered}</Component>;
  }

  return <>{rendered}</>;
};

BlockSectionNestedBlock.displayName = "BlockSectionNestedBlock";
